import { useCallback, useMemo, useState, useRef } from 'react'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { FullView, FullViewContent } from '../../../../components/Modal'
import { ActionButton } from '../../../../components/Button'
import { Check, ChevronBackward } from '@nine-thirty-five/material-symbols-react/rounded'
import { type TaskFormData, useCreateTask } from '@zendia/frontend-shared'
import { axiosRequest } from '../../../../utilities/axiosClient'
import {
  parseTasksPanelTab,
  type TasksPanelTab,
} from '../../../../components/dashboard/tasksPanelTab'
import { TaskForm, type TaskFormRef } from '../../../../components/Task'

export const Route = createFileRoute('/_authenticated/tasks/new/form')({
  component: RouteComponent,
  staticData: { suppressNotifications: true },
  validateSearch: (
    search,
  ): {
    projectId?: string
    dashboardTab?: TasksPanelTab
    fromAllTasks?: boolean
    goalId?: string
    milestoneId?: string
  } => ({
    // Lets a caller pre-select a project.
    projectId: search.projectId as string | undefined,
    // Which dashboard Tasks panel group the user was on before creating this task, so
    // saving (or discarding) returns the dashboard to that same group.
    dashboardTab: parseTasksPanelTab(search.dashboardTab),
    // Set when adding a task from the All Tasks page, so saving (or discarding) returns there
    fromAllTasks: search.fromAllTasks === true || undefined,
    // Set when adding a task from a goal or milestone: pre-selects it in the Goal picker, and
    // saving (or discarding) returns to its Your Plan, on that milestone.
    goalId: search.goalId as string | undefined,
    milestoneId: search.milestoneId as string | undefined,
  }),
})

function RouteComponent() {
  const router = useRouter()
  const { mutateAsync: createTask } = useCreateTask(axiosRequest)
  const { projectId, dashboardTab, fromAllTasks, goalId, milestoneId } = Route.useSearch()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string>()
  const taskFormRef = useRef<TaskFormRef>(null)
  const initialValues = useMemo(
    () => ({
      ...(projectId ? { projectId } : {}),
      ...(goalId ? { goal: { goalRefinementId: goalId, milestoneId } } : {}),
    }),
    [projectId, goalId, milestoneId],
  )

  const leave = useCallback(() => {
    if (fromAllTasks) {
      router.navigate({ to: '/tasks', search: { dashboardTab }, replace: true })
    } else if (goalId) {
      router.navigate({
        to: '/goals/$goalId',
        params: { goalId },
        search: { tab: 'plan', planMilestone: milestoneId },
        replace: true,
      })
    } else {
      router.navigate({ to: '/dashboard', search: { tab: dashboardTab }, replace: true })
    }
  }, [router, fromAllTasks, goalId, milestoneId, dashboardTab])

  const onBack = useCallback(() => {
    if (isSubmitting) return
    leave()
  }, [isSubmitting, leave])

  const onSave = useCallback(
    async (data: TaskFormData) => {
      setIsSubmitting(true)
      setSubmitError(undefined)

      try {
        await createTask(data)
        leave()
      } catch (error) {
        console.error(error)
        setSubmitError('Failed to create task')
      } finally {
        setIsSubmitting(false)
      }
    },
    [createTask, leave],
  )

  return (
    <FullView
      leftAction={
        <ActionButton
          onClick={onBack}
          label="Discard"
          icon={<ChevronBackward className="size-6" />}
        />
      }
      centerContent={<div>New Task</div>}
      rightAction={
        <ActionButton
          primary
          onClick={() => {
            taskFormRef.current?.submit()
          }}
          label="Save task"
          disabled={isSubmitting}
          icon={<Check className="m-auto size-6" />}
        />
      }
    >
      <FullViewContent>
        <div className="h-full">
          <TaskForm
            ref={taskFormRef}
            initialValues={initialValues}
            onSubmit={onSave}
            isSubmitting={isSubmitting}
            submitError={submitError}
          />
        </div>
      </FullViewContent>
    </FullView>
  )
}
